'use client';

import { usePathname, useRouter } from '@/navigation';
import { LOCALES } from '@/tolgee/shared';
import { Box, IconButton, MenuItem, MenuList, Popover } from '@mui/material';
import { useLocale } from 'next-intl';
import { ElementType, type MouseEvent, memo, useCallback, useMemo, useState, useTransition } from 'react';
import TwemojiFlagChina from './icons/TwemojiFlagChina';
import TwemojiFlagUnitedKingdom from './icons/TwemojiFlagUnitedKingdom';

interface FlagIconProps {
  countryCode: string;
  size?: number;
}

interface LanguageOption {
  value: string;
  label: string;
  countryCode: string;
}

// 国旗图标映射 - 直接使用iconify图标名称
const FLAG_ICON_MAP: Record<string, ElementType> = {
  CN: TwemojiFlagChina,
  GB: TwemojiFlagUnitedKingdom,
};

// 获取国旗图标名称
const getFlagIconName = (countryCode: string): ElementType => {
  return FLAG_ICON_MAP[countryCode.toUpperCase()] || TwemojiFlagUnitedKingdom;
};

// 优化的国旗图标组件 - 使用 memo 防止不必要的重新渲染
const FlagIcon = memo<FlagIconProps>(({ countryCode, size = 26 }) => {
  const iconName = getFlagIconName(countryCode);
  const containerStyle = useMemo(
    () => ({
      width: size,
      height: size * 0.77, // 保持国旗比例
      flexShrink: 0,
    }),
    [size],
  );

  return (
    <div className="flex items-center justify-center overflow-hidden rounded-md bg-n-6/20" style={containerStyle}>
      <Box component={iconName} width={size} height={size * 0.77} style={{ display: 'block' }} />
    </div>
  );
});

// 设置显示名称用于调试
FlagIcon.displayName = 'FlagIcon';

/**
 * 现代化语言切换器组件
 * 使用 Popover 风格交互，适配暗夜模式设计
 */
function LanguageSwitcher() {
  const router = useRouter();
  const pathname = usePathname();
  const locale = useLocale();
  const [_, startTransition] = useTransition();

  // Popover 状态管理
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  // 获取当前语言信息
  const currentLang = LOCALES.find((lang: LanguageOption) => lang.value === locale);

  // 打开 Popover
  const handleOpen = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  // 关闭 Popover
  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  // 切换语言
  const handleLanguageChange = useCallback(
    (newLocale: string) => {
      try {
        startTransition(() => {
          router.push(pathname, { locale: newLocale });
        });
        handleClose();
      } catch (error) {
        console.error('Language switch error:', error);
        // 降级方案
        const newUrl = `/${newLocale}${pathname}`;
        window.location.href = newUrl;
      }
    },
    [router, pathname, handleClose],
  );

  return (
    <>
      <IconButton
        onClick={handleOpen}
        sx={{
          p: 0,
          width: 40,
          height: 40,
          borderRadius: '12px',
          backgroundColor: 'rgba(36, 39, 49, 0.85)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(12px)',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          // 赛博朋克风格的内发光
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: '12px',
            padding: '1px',
            background: 'linear-gradient(135deg, rgba(250, 148, 28, 0.3), rgba(111, 67, 243, 0.3))',
            mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            maskComposite: 'xor',
            opacity: 0,
            transition: 'opacity 0.3s ease',
          },
          ...(open && {
            backgroundColor: 'rgba(36, 39, 49, 0.95)',
            borderColor: 'rgba(250, 148, 28, 0.6)',
            boxShadow: '0 0 25px rgba(250, 148, 28, 0.3), inset 0 0 15px rgba(250, 148, 28, 0.1)',
            '&::before': {
              opacity: 1,
            },
          }),
          '@media (hover: hover)': {
            '&:hover': {
              backgroundColor: 'rgba(36, 39, 49, 0.95)',
              borderColor: 'rgba(255, 255, 255, 0.25)',
              transform: 'scale(1.05)',
              boxShadow: '0 0 15px rgba(255, 255, 255, 0.1)',
              '&::before': {
                opacity: 0.7,
              },
            },
          },
          '&:active': {
            transform: 'scale(0.95)',
            boxShadow: '0 0 10px rgba(250, 148, 28, 0.4)',
          },
        }}
        aria-label="Switch language"
      >
        <FlagIcon countryCode={currentLang?.countryCode || 'GB'} size={28} />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        disableScrollLock
        keepMounted
        slotProps={{
          paper: {
            sx: {
              width: 160,
              overflow: 'hidden',
              mt: 1,
              borderRadius: '16px',
              backgroundColor: 'rgba(21, 19, 29, 0.95)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(20px)',
              position: 'relative',
              // 赛博朋克风格的外发光和阴影
              boxShadow: `
                0 10px 40px rgba(0, 0, 0, 0.5),
                0 0 30px rgba(250, 148, 28, 0.15),
                inset 0 1px 0 rgba(255, 255, 255, 0.1)
              `,
              // 添加渐变边框效果
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                borderRadius: '16px',
                padding: '1px',
                background:
                  'linear-gradient(135deg, rgba(250, 148, 28, 0.4), rgba(111, 67, 243, 0.4), rgba(250, 148, 28, 0.4))',
                mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                maskComposite: 'xor',
                opacity: 0.6,
              },
            },
          },
        }}
      >
        <MenuList sx={{ p: 1 }}>
          {LOCALES.map((option) => (
            <MenuItem
              key={option.value}
              selected={option.value === currentLang?.value}
              onClick={() => handleLanguageChange(option.value)}
              sx={{
                gap: 2,
                py: 1.5,
                px: 2,
                borderRadius: '12px',
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '14px',
                fontWeight: 500,
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                // 添加扫描线效果
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(250, 148, 28, 0.1), transparent)',
                  transition: 'left 0.5s ease',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(250, 148, 28, 0.15)',
                  color: 'rgba(250, 148, 28, 1)',
                  borderLeft: '3px solid rgba(250, 148, 28, 0.8)',
                  boxShadow: 'inset 0 0 15px rgba(250, 148, 28, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(250, 148, 28, 0.25)',
                    boxShadow: 'inset 0 0 20px rgba(250, 148, 28, 0.15)',
                  },
                  '&::before': {
                    left: '100%',
                  },
                },
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  transform: 'translateX(4px)',
                  borderLeft: '2px solid rgba(111, 67, 243, 0.6)',
                  boxShadow: 'inset 0 0 10px rgba(111, 67, 243, 0.1)',
                  '&::before': {
                    left: '100%',
                  },
                },
                '&:active': {
                  transform: 'translateX(2px) scale(0.98)',
                },
              }}
            >
              <FlagIcon countryCode={option.countryCode} size={28} />
              {option.label}
            </MenuItem>
          ))}
        </MenuList>
      </Popover>
    </>
  );
}

export default LanguageSwitcher;
